import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { waitForPendingWrites } from 'firebase/firestore';
import { db, isConfigured } from '../lib/firebase';
import {
  onWriteRefused, recoverUnqueuedWrites, startWriteQueue, writeQueue,
} from '../data/writes';
import {
  outstandingCount, outstandingCountFor, outstandingWritesFor, studioOfWrite,
  onOutstandingChange, clearWrite,
} from '../data/outstandingWrites';
import { onStaleReadsChange, staleReadCount } from '../data/staleReads';
import { awaitingServerCount, onAwaitingServerChange } from '../data/serverAnswers';
import {
  FLASH,
  holdMillis,
  onConnectivity,
  onAwaitingServer,
  onHoldExpired,
  onPendingWrites,
  onRefusalsDismissed,
  onStaleReads,
  onUnconfirmedDismissed,
  onWriteRefused as refuse,
  onWritesUnconfirmed,
  syncMachine,
  syncState as stateOf,
} from '../lib/syncMachine';
import { useAuth } from './AuthContext';
import { reportClientEvent, queueStall, stallCheckMs } from '../lib/clientEvents';
import { CLIENT_EVENTS } from '../domain/clientEvents';

const SyncContext = createContext(null);

// When this tab started: the line between "outstanding" and "unconfirmed"
// (#167). A record written before this instant belongs to a session that is
// gone, so its callback is gone with it; one written after belongs to a promise
// this tab still holds. Comparing against it stops the check below reporting a
// write that is one microtask away from clearing itself.
const SESSION_STARTED_AT = Date.now();

/**
 * The console line for notes from an earlier page that no queue covers (#167,
 * #585).
 *
 * The check sees a note written before this page loaded, naming paths no live
 * batch will write, with no copy of the write to resend. It cannot see whether
 * the server ever had the write, so the message names both readings. Exported
 * so a test can pin the wording.
 */
export function unconfirmedMessage(count) {
  return `[sync] ${count} write(s) were noted by an earlier page and are in no write queue now, `
    + 'and their notes carry no copy to resend. Two cases this cannot tell apart: the server '
    + 'took the write and that page closed before clearing the note, or that page closed before '
    + 'the write was stored and it was never sent (possible for a note written before #585, or '
    + 'one whose copy did not fit or was refused — see any [writes] line above). Check these '
    + 'documents (#167):';
}

/**
 * The dead batches belonging to one teacher.
 *
 * The queue is one per origin, so its dead letters are not necessarily this
 * session's (#282): a batch teacher A made on this laptop is still there, and
 * nothing teacher B does can settle it.
 *
 * `deadLetters()` carries `{ batchId, subject, docCount, attempts, reason }`
 * and no path, so the studio comes from `records()`, which carries the ops.
 * `studios.get(batchId)` has three answers:
 *
 *   * a **uid** — the batch's own studio;
 *   * **`null`** — `studioOfWrite` refused to guess: no paths, an unparseable
 *     path, or a batch naming two studios;
 *   * **`undefined`** — no record behind the letter. The queue is one per
 *     origin and a letter outlives the record that names its paths, so this
 *     is common (`test/integration/syncRefusal.test.jsx`).
 *
 * The last two are returned separately and logged, never shown as "your change
 * was refused": whose they are is not knowable (#278).
 *
 * @returns `{ mine, unattributable, foreign, gaps }`. `mine` and `gaps` are
 *          safe to show this teacher; `foreign` is a count of what this
 *          *device* is holding for other studios and names none of their work.
 */
async function deadLettersFor(uid) {
  const queue = writeQueue();
  const [letters, records, gaps] = await Promise.all([
    queue.deadLetters(), queue.records(), queue.causalGaps(),
  ]);
  const studios = new Map(records.map(
    (r) => [r.batchId, studioOfWrite({ paths: r.ops.map((o) => o.path) })],
  ));
  const mine = [];
  const unattributable = [];
  letters.forEach((letter) => {
    const studio = studios.get(letter.batchId);
    if (studio === uid && uid) mine.push(letter);
    else if (studio === undefined || studio === null) unattributable.push(letter);
  });

  // #300: what this device is holding for somebody else. A batch whose studio
  // is neither this teacher's nor unknowable is parked by `writes.js`'s
  // dispatch guard until its teacher signs back in on this browser (#283).
  //
  // Dead ones are excluded: this counts what is owed, and a dead batch is not
  // re-dispatched without a person saying so — `owedPaths()`'s rule.
  const foreignStudios = new Set();
  let foreignOwed = 0;
  records.forEach((record) => {
    if (record.state === 'dead') return;
    const studio = studios.get(record.batchId);
    if (!studio || studio === uid) return;
    foreignOwed += 1;
    foreignStudios.add(studio);
  });

  // #206: documents a dead batch left a hole in the history of. The core
  // reports these and refuses to resolve them. Scoped like the letters: a gap
  // names a path, so its studio is the path's.
  const myGaps = uid
    ? gaps.filter((gap) => studioOfWrite({ paths: [gap.path] }) === uid)
    : [];

  return {
    mine,
    unattributable,
    foreign: { count: foreignOwed, studios: [...foreignStudios] },
    gaps: myGaps,
  };
}

/**
 * Make the chip's refusal count say what the list says (#206).
 *
 * Recomputed from the list rather than decremented: `machine.refusedCount` is
 * incremented by two independent channels — the live `onWriteRefused` and the
 * scan at sign-in — so subtracting one per discard would drift when a batch
 * arrived by both routes.
 *
 * `reported` is re-seeded with what remains, so the next scan knows these have
 * been counted.
 */
function republish(setMachine, reported, report) {
  reported.current = new Set(report.mine.map((letter) => letter.batchId));
  setMachine((m) => report.mine.reduce(
    (next, letter) => refuse(next, letter.subject),
    onRefusalsDismissed(m),
  ));
}

export function SyncProvider({ children }) {
  const { user, loading } = useAuth();
  // The whole chip state — count, connectivity and which flash is being held —
  // as one immutable value (#81; see the header of src/lib/syncMachine.js).
  const [machine, setMachine] = useState(() =>
    syncMachine(typeof navigator !== 'undefined' ? navigator.onLine : true)
  );
  // Online/offline
  useEffect(() => {
    const onUp = () => setMachine((m) => onConnectivity(m, true));
    const onDown = () => setMachine((m) => onConnectivity(m, false));
    window.addEventListener('online', onUp);
    window.addEventListener('offline', onDown);
    return () => {
      window.removeEventListener('online', onUp);
      window.removeEventListener('offline', onDown);
    };
  }, []);

  // Batches already reported to the chip as dead, so the scan below cannot
  // count one this session has just watched die. A ref: nothing renders from
  // it, and it must survive the uid effect re-running.
  const reported = useRef(new Set());

  // The list behind the chip's sentence (#206) — each batch with its
  // `docCount`, `attempts` and reason, which is what a teacher decides on — and
  // the note about work this device owes somebody else (#300).
  const [deadLetters, setDeadLetters] = useState([]);
  const [causalGaps, setCausalGaps] = useState([]);
  const [foreignOwed, setForeignOwed] = useState({ count: 0, studios: [] });

  /**
   * Ask the queue again and publish what it says. Called by the scan below
   * when auth resolves, after a live refusal, and after every control that
   * changes the answer. Returns the scan so a caller can act on it.
   */
  const rescan = useCallback(async () => {
    const report = await deadLettersFor(user?.uid ?? null);
    setDeadLetters(report.mine);
    setCausalGaps(report.gaps);
    setForeignOwed(report.foreign);
    return report;
  }, [user?.uid]);

  // The sixth state (#162): the server permanently refused a write.
  //
  // Subscribed for the life of the provider, not of a drawer: offline, a commit
  // does not settle until the network returns, so the rejection arrives after
  // the form that issued it has closed (see the header of src/data/writes.js).
  // Outside the `[user]` effect too: a refusal during a sign-out must still be
  // reported.
  //
  // Each refusal rescans, because `deadLetters`, `causalGaps` and `foreignOwed`
  // are fed only by `rescan`. `queue.js` patches the `dead` row before calling
  // `onRefused`, so a scan from inside this handler sees the row. A rescan
  // rather than appending: the handler gets a refusal, not a dead-letter row,
  // and `deadLettersFor`'s scoping (#282) must not be reimplemented.
  //
  // `rescan` changes identity with the uid, so the handler reaches it through a
  // ref assigned during render (as `hasReadRef` in data/useTags.jsx).
  const rescanRef = useRef(rescan);
  rescanRef.current = rescan;
  useEffect(
    () => onWriteRefused(({ subject, batchId, code, reason }) => {
      if (batchId) reported.current.add(batchId);
      setMachine((m) => refuse(m, subject));
      // And to the server (#459): the noun and the code, never the path or
      // the document.
      reportClientEvent(CLIENT_EVENTS.writeRefused, { subject, code, reason });
      // Two-argument `then`: the rejection handler is for the queue's own
      // failure, not a bug in `rescan` (#184).
      rescanRef.current().then(undefined, (err) => {
        console.error('[sync] a write was refused, but the dead-letter list could not be re-read:', err);
      });
    }),
    []
  );

  // Writes that died where nobody could see them (#206).
  //
  // The live channel above covers only deaths this tab witnessed. A batch is
  // replayed from IndexedDB in sessions that never issued it, so a death with
  // no tab open, or before a reload, leaves only a `dead` row in IndexedDB.
  //
  // So the dead-letter list is asked once, when auth resolves — not on every
  // queue change: `deadLetters()` is a `store.all()` plus a wasm call, and a
  // death during this session has the channel above. Scoped to the signed-in
  // teacher (#282) by `deadLettersFor`.
  useEffect(() => {
    if (loading) return undefined;
    let live = true;
    const scan = async () => {
      const { mine: owned, unattributable } = await rescan();
      if (!live) return;
      // Logged, never shown: whose these are is not knowable (see
      // `deadLettersFor`, #278).
      if (unattributable.length) {
        console.warn(
          `[sync] ${unattributable.length} dead write(s) belong to no identifiable studio,`
          + ' so they are not being attributed to the signed-in teacher (#206):',
          unattributable.map((l) => `${l.batchId} (${l.reason}, ${l.attempts} attempts)`),
        );
      }
      const mine = owned.filter((letter) => !reported.current.has(letter.batchId));
      if (!mine.length) return;
      console.warn(
        `[sync] ${mine.length} write(s) died with nobody listening (#206):`,
        mine.map((l) => `${l.batchId} (${l.reason}, ${l.attempts} attempts)`),
      );
      mine.forEach((letter) => reported.current.add(letter.batchId));
      setMachine((m) => mine.reduce((next, letter) => refuse(next, letter.subject), m));
    };
    // Two-argument `then`: the rejection handler is for the queue's own failure
    // — an IndexedDB that will not open, a wasm that will not load — not for a
    // bug in the body above (#184).
    scan().then(undefined, (err) => {
      console.error('[sync] could not read the dead-letter list:', err);
    });
    return () => { live = false; };
  }, [loading, rescan]);

  /**
   * Put every dead batch back in the queue. `reported` is cleared so that a
   * batch which dies again is reported again.
   */
  const retryDeadLetters = useCallback(async () => {
    // `mine` only: re-queueing a batch whose studio is unknowable would
    // dispatch it under whoever is signed in now (#282).
    const { mine: letters } = await deadLettersFor(user?.uid ?? null);
    reported.current = new Set();
    setMachine(onRefusalsDismissed);
    // Sequential: `retry` drains, and concurrent drains would race over the
    // same scheduling lock.
    for (const letter of letters) {
      await writeQueue().retry(letter.batchId);
    }
    await rescan();
  }, [user?.uid, rescan]);

  /**
   * Put one dead batch back, named by the teacher from the list. `batchId`
   * arrives from a click; it is retried only if it is in this teacher's list.
   */
  const retryDeadLetter = useCallback(async (batchId) => {
    const { mine } = await deadLettersFor(user?.uid ?? null);
    if (!mine.some((letter) => letter.batchId === batchId)) return;
    reported.current.delete(batchId);
    await writeQueue().retry(batchId);
    republish(setMachine, reported, await rescan());
  }, [user?.uid, rescan]);

  /**
   * Give up on one dead batch (#206).
   *
   * Without it, a batch the server will never accept is reported at every
   * sign-in, "Try again" brings the same refusal back, and its row stays in
   * IndexedDB for the life of the origin. MMS has no offline queue and so no
   * such control (`SyncChip.jsx`).
   *
   * Constraints that keep it safe:
   *
   *   * per batch and from the list, never beside the dismiss — the row says
   *     the subject, document count, attempts and reason;
   *   * scoped to this teacher's own letters, like retry, so a click cannot
   *     destroy the previous teacher's work on a shared laptop;
   *   * reachable only for a batch that is already dead: the list is
   *     `deadLetters()`.
   *
   * It cannot be undone. `queue.discard` reports the batch as `discarded`
   * through `onSettled`, which clears the outstanding-write note in
   * `writes.js`, so the chip's count comes down with it.
   */
  const discardDeadLetter = useCallback(async (batchId) => {
    const { mine } = await deadLettersFor(user?.uid ?? null);
    // Not this teacher's, or not dead.
    if (!mine.some((letter) => letter.batchId === batchId)) return;
    await writeQueue().discard(batchId);
    reported.current.delete(batchId);
    republish(setMachine, reported, await rescan());
  }, [user?.uid, rescan]);

  // The pending count (#169, #170), from `outstandingWrites.js` rather than
  // from `onSnapshot` listeners: a `limit(N)` listener cannot see a pending
  // write outside its window (test/integration/pendingWriteReach.test.jsx).
  // `src/data/writes.js` notes every write before issuing it, so the count
  // covers every collection, has no window, survives a reload and is shared
  // across tabs.
  //
  // Scoped to the signed-in teacher (#282): the notes are per origin, and a
  // previous teacher's batches are still owed and parked for their return, so
  // the count is scoped rather than the notes deleted (see
  // `outstandingWritesFor`).
  //
  // `loading` is the pre-auth window and counts everything: reading `user?.uid
  // ?? null` before `onAuthStateChanged` has answered would say "signed out"
  // and blank the chip on every first paint.
  //
  // Not `queue.depth()` (#206): that is an async IndexedDB read where this is a
  // synchronous `localStorage` read that can run in a render, and it counts
  // every live batch on the origin rather than this teacher's.
  useEffect(() => {
    const uid = user?.uid ?? null;
    const count = () => (loading ? outstandingCount() : outstandingCountFor(uid));
    const push = () => setMachine((m) => onPendingWrites(m, count()));
    push();
    return onOutstandingChange(push);
  }, [loading, user?.uid]);

  // The offline promise failing quietly (#459): this studio's writes owed for
  // longer than the core's threshold while the browser says it is online.
  // Checked every `stallCheckMs()` rather than on queue changes, because a
  // stall is the absence of change; reported once a page load (the event's
  // key), and only this teacher's batches count (#282).
  useEffect(() => {
    const uid = user?.uid;
    if (!uid) return undefined;
    const check = () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      let records;
      try {
        records = writeQueue().records();
      } catch {
        return;
      }
      Promise.resolve(records).then((all) => {
        const stall = queueStall(all, { uid, now: Date.now() });
        if (stall) reportClientEvent(CLIENT_EVENTS.syncStalled, stall);
      }, () => {});
    };
    const timer = setInterval(check, stallCheckMs());
    return () => clearInterval(timer);
  }, [user?.uid]);

  // Reads being served from disk with no snapshot behind them (#207 U6). The
  // same shape as the pending count: `src/data/staleReads.js` is written to by
  // the two read-path hooks as they read, so every provider reports without a
  // screen subscribing to each one (#169).
  useEffect(() => {
    const push = () => setMachine((m) => onStaleReads(m, staleReadCount()));
    push();
    return onStaleReadsChange(push);
  }, []);

  // Lists waiting for the server (#682): answered only by a cache that never
  // had the server's answer, and holding nothing. The same registry #651's
  // *Not loaded on this device yet* is drawn from, so the chip and the screen
  // read one fact.
  useEffect(() => {
    const push = () => setMachine((m) => onAwaitingServer(m, awaitingServerCount()));
    push();
    return onAwaitingServerChange(push);
  }, []);

  // Writes from a session that is gone (#167, #278).
  //
  // When `waitForPendingWrites` resolves, a note from *before this tab started*
  // is a candidate for a write the server answered while nobody listened. Our
  // queue sits in front of the SDK, so an empty SDK queue does not mean the
  // write was sent:
  //
  //   * **The queue is asked.** `owedPaths()` is every path a live batch will
  //     still write, so a note covering only owed paths is work in hand. It is
  //     matched by path rather than batch id because the note is written before
  //     the enqueue, so in the window this check is for no batch exists yet.
  //   * The queue hands every write to the SDK whether or not the browser
  //     thinks it is online (#277), so `waitForPendingWrites` does not resolve
  //     over an unsent write; the filter covers the window between
  //     `recordWrite` and the dispatch reaching the SDK.
  //
  // With `liteWriteFlag.js` on (off by default, #410), writes never enter the
  // SDK's mutation queue, so `waitForPendingWrites` resolves at once and the
  // durable filter carries the check: `owedPaths()` reads IndexedDB and only
  // notes with `r.at < SESSION_STARTED_AT` are considered.
  // `test/ui/unconfirmedWrites.test.jsx` stubs the gate open and covers that
  // state.
  //
  // Only the records reported are cleared: the other notes are for writes this
  // tab still holds a promise for.
  //
  // `recoverUnqueuedWrites` runs first (#585): a note carries a copy of its
  // batch until the queue's record is durable, and a page that went away in
  // that gap had its batch put back in the queue, where `owedPaths` covers it.
  // What remains reported is a bare note — no copy, or a copy the queue would
  // not take back — which is either a write acknowledged before its tab died
  // or one never sent; the message says both. A false alarm asks about
  // something that was fine rather than dropping something that was not.
  //
  // Scoped to the signed-in teacher's notes (#282), like the pending count.
  // `loading` gates it as it gates `startWriteQueue`: before
  // `onAuthStateChanged` answers, "signed out" and "nobody has said" differ.
  useEffect(() => {
    if (!isConfigured || !db || loading) return undefined;
    let live = true;
    // `then(onResolve, onReject)` (#184): a trailing `.catch` would also
    // swallow anything the success handler throws. The queue is read inside the
    // handler rather than chained, so an IndexedDB failure is not routed into
    // the arm written for "the client was terminated".
    const report = async () => {
      // A note this is about to resend is not a note to judge (#585). Shared
      // with `startWriteQueue` and run once per page, so this awaits work
      // already under way.
      await recoverUnqueuedWrites();
      const owed = await writeQueue().owedPaths();
      if (!live) return;
      const stale = outstandingWritesFor(user?.uid ?? null).filter((r) => r.at < SESSION_STARTED_AT
        // A note with no paths cannot be matched against the queue, so it is
        // reported. `writes.js` always carries the paths.
        && !(r.paths.length && r.paths.every((p) => owed.has(p))));
      if (!stale.length) return;
      // `unconfirmedMessage` is the wording (#585), pinned by
      // `test/ui/unconfirmedWrites.test.jsx`.
      console.warn(unconfirmedMessage(stale.length), stale.map((r) => r.paths).flat());
      setMachine((m) => onWritesUnconfirmed(m, stale.length));
      // Only what was reported; other notes belong to promises this tab still
      // holds (#278).
      stale.forEach((r) => clearWrite(r.id));
    };
    waitForPendingWrites(db).then(
      // Returned: a rejection from `report` surfaces as an unhandled
      // rejection.
      () => (live ? report() : undefined),
      // Offline this never settles. A rejection means the client was
      // terminated, and there is nothing to report then.
      () => {},
    );
    return () => { live = false; };
  }, [loading, user?.uid]);

  // Start the queue, and keep it started across a reconnect (#277). Here
  // because this provider is mounted for the life of the app; `writes.js` owns
  // what the nudge is for.
  //
  // `db` is passed because `writes.js` learns the `Firestore` handle from the
  // first call that carries one, and a batch restored from IndexedDB is not a
  // call — a session that replays before any save would have no handle.
  //
  // `user?.uid` (#282): the queue is one per origin and outlives a sign-out, so
  // teacher A's offline batch is still there when B signs in. `writes.js` holds
  // a batch that belongs to another studio rather than dispatching it under B;
  // keyed on the user so a sign-in replaces the answer.
  //
  // `loading` gates it: `useAuth().user` is null both before
  // `onAuthStateChanged` has answered and after it has said nobody, and
  // telling `writes.js` "signed out" during the first would hold every
  // inherited batch. Nothing is dispatched during the wait, because nothing
  // has called `drain()`.
  //
  // `wake()` (#284): a batch held for another studio waits on that teacher
  // signing in, which the queue cannot see, so it parks on an escalating timer.
  // This tells it the answer may have changed.
  useEffect(() => {
    if (loading) return;
    startWriteQueue(db, user?.uid ?? null);
    // Two-argument `then`: the rejection handler is for the wake's own failure.
    // `drain` does not throw for an ordinary write failure, so anything here is
    // a bug in the loop.
    writeQueue().wake().then(undefined, (err) => {
      console.error('[sync] waking the write queue failed:', err);
    });
  }, [loading, user?.uid]);

  // When the machine says it is holding a flash open, wait that long and tell
  // it the hold expired.
  //
  // `hold` is a function of the flash phase alone — null, 1200, 2000 — so this
  // effect re-runs exactly when the phase changes (#81). A connectivity flip
  // mid-flash re-renders without touching the timer. Android serves the hold
  // again from the top after a flip, so there a flash spanning a flap lasts
  // slightly longer than its constant.
  const hold = holdMillis(machine);
  useEffect(() => {
    if (hold === null) return undefined;
    const timer = setTimeout(() => setMachine(onHoldExpired), hold);
    return () => clearTimeout(timer);
  }, [hold]);

  const pendingCount = machine.pendingCount;
  const isOnline = machine.online;
  const syncState = stateOf(machine);

  const value = {
    pendingCount,
    isOnline,
    // Names for parts of the machine; the chip reads `syncState`.
    isSyncing: machine.flash === FLASH.syncing,
    justSaved: machine.flash === FLASH.saved,
    syncState,
    refusedCount: machine.refusedCount,
    refusedSubject: machine.refusedSubject,
    // The two ways out of `refused`; no timer ends it (see syncMachine.js).
    // Dismiss hides the label and leaves the batch dead, and the scan above
    // reports it again next session. Discarding is per batch, from the list
    // (`discardDeadLetter`).
    dismissRefusals: () => setMachine(onRefusalsDismissed),
    retryDeadLetters,
    // #206: the list itself, and the two things to do about one entry.
    // `SyncChip.jsx` renders these behind a disclosure rather than beside the
    // dismiss (see `discardDeadLetter`).
    deadLetters,
    retryDeadLetter,
    discardDeadLetter,
    // Documents a dead batch left a hole in the history of. Reported, never
    // resolved: the core can see the order was broken and cannot see whether it
    // mattered, and a person can.
    causalGaps,
    // #300: unsent work this *device* is holding for a studio that is not the
    // signed-in one. A count and the number of studios, never the work itself.
    foreignOwedCount: foreignOwed.count,
    foreignOwedStudios: foreignOwed.studios.length,
    unconfirmedCount: machine.unconfirmedCount,
    dismissUnconfirmed: () => setMachine(onUnconfirmedDismissed),
    // #207 U6. No dismissal: `stale` is a condition rather than an event, and
    // it ends when a snapshot lands rather than when a teacher acknowledges it.
    staleReadCount: machine.staleReadCount,
    // #682. A condition like `stale`, with no dismissal.
    awaitingCount: machine.awaitingCount,
  };

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

export function useSyncStatus() {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSyncStatus must be used within SyncProvider');
  return ctx;
}
